'use client'

import { useState } from 'react'
import { useKeenSlider } from 'keen-slider/react'
import Link from 'next/link'

import SimpleImage from '@/components/image/SimpleImage'
import { KeenSliderArrows } from '@/components/keen-slider/KeenSliderPlugins'

import 'keen-slider/keen-slider.min.css'

import type { WP_API_ACFImageResponse } from '@/types/wp-api-acf'

type LogoCarouselProps = {
  heading?: string
  logos: {
    logo: WP_API_ACFImageResponse
    link: string
  }[]
}

export default function LogoCarousel({ heading, logos }: LogoCarouselProps): JSX.Element | null {
  const [mounted, setMounted] = useState(false)
  const [isDisabled, setIsDisabled] = useState(false)
  const [sliderRef, instanceRef] = useKeenSlider({
    initial: 0,
    drag: true,
    slides: {
      perView: 8,
      spacing: 52,
    },
    breakpoints: {
      '(max-width: 1519px)': {
        slides: {
          perView: 6,
          spacing: 52,
        },
      },
      '(max-width: 1199px)': {
        slides: {
          perView: 4,
          spacing: 32,
        },
      },
      '(max-width: 989px)': {
        slides: {
          perView: 3,
          spacing: 32,
        },
      },
      '(max-width: 600px)': {
        slides: {
          perView: 2,
          spacing: 24,
        },
      },
    },
    created(s) {
      setIsDisabled(s.options.disabled)
      setMounted(true)
    },
    detailsChanged(s) {
      setIsDisabled(s.options.disabled)
    },
  })

  if (!logos?.length) {
    return null
  }

  const logoItemClass = 'aspect-square max-h-[120px] w-full object-contain'
  const visibilityClass = mounted ? 'visible' : 'invisible'

  const logoItems = logos.map((item, index) => {
    const key = `${item.logo.ID}-${index}`

    if (!item.logo?.url) {
      return null
    }

    return (
      <li key={key} className="keen-slider__slide">
        {item.link ? (
          <Link href={item.link}>
            <SimpleImage
              path={item.logo.url}
              transforms={{
                width: '240',
                height: '240',
                focus: 'center',
                crop: 'at_max',
              }}
              alt={item.logo.alt}
              className={`${logoItemClass} ${visibilityClass} transition-all duration-500 ease-in-out hover:-translate-y-[2px]`}
            />
          </Link>
        ) : (
          <SimpleImage
            path={item.logo.url}
            transforms={{
              width: '240',
              height: '240',
              focus: 'center',
              crop: 'at_max',
            }}
            alt={item.logo.alt}
            className={`${logoItemClass} ${visibilityClass}`}
          />
        )}
      </li>
    )
  })

  return (
    <>
      <section className="mt-m flex gap-x-xxl s:flex-col s-up:items-center">
        {heading ? <span className="whitespace-nowrap s-up:min-w-[50px]">{heading}</span> : null}

        <ul ref={sliderRef} className="keen-slider flex items-center justify-start">
          {logoItems}
        </ul>
      </section>

      {mounted ? <KeenSliderArrows sliderInstance={instanceRef} disabled={isDisabled} /> : null}
    </>
  )
}
